// Granular cookie/tracking consent state for Crayo.
//
// Stored under localStorage `crayo-consent-v2`. The legacy key
// `crayo-cookies` (values 'necessary' | 'all') is intentionally NOT
// migrated — when consent categories materially change, GDPR requires a
// fresh prompt. Existing users will see the banner again exactly once.
//
// Shape:
//   {
//     necessary: true,
//     analytics: boolean,
//     marketing: boolean,
//     ts: number  // epoch ms when consent was recorded
//   }
//
// `necessary` is always true and not user-toggleable: localStorage for
// session ids, Sentry error capture, and Stripe redirects are required
// for the product to work and don't carry GDPR consent obligations
// (legitimate interest + contract performance, not analytics/marketing).
//
// Loaders for Pixel and GA4 listen for the `crayo-consent-changed`
// CustomEvent on `window` to react to user-driven consent updates
// without a full page reload — except Pixel revocation, which Meta's
// SDK doesn't fully support without a reload (see meta-pixel-init.js).

const STORAGE_KEY = 'crayo-consent-v2';
const CHANGE_EVENT = 'crayo-consent-changed';

/**
 * @typedef {Object} ConsentState
 * @property {true} necessary
 * @property {boolean} analytics
 * @property {boolean} marketing
 * @property {number} ts
 */

/** @type {ConsentState} */
const DEFAULT_DENIED = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
  ts: 0,
});

/**
 * Read the stored consent record, or null if none exists yet.
 * Returns null when the user has not interacted with the banner.
 * @returns {ConsentState | null}
 */
export function getConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      necessary: true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      ts: typeof parsed.ts === 'number' ? parsed.ts : 0,
    };
  } catch {
    return null;
  }
}

/**
 * Persist a new consent record and notify subscribers.
 *
 * @param {{ analytics?: boolean, marketing?: boolean }} patch
 */
export function setConsent(patch) {
  if (typeof window === 'undefined') return;
  const current = getConsent() ?? DEFAULT_DENIED;
  const next = {
    necessary: true,
    analytics: patch.analytics === true || (patch.analytics === undefined && current.analytics),
    marketing: patch.marketing === true || (patch.marketing === undefined && current.marketing),
    ts: Date.now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota / privacy mode: state is in-memory only this session. Loaders
    // dispatched below will still see the new value via the event payload.
  }
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  } catch {
    // CustomEvent unsupported (very old browsers); subscribers won't react
    // until the next page load. Acceptable.
  }
}

/**
 * Reset consent so the banner re-prompts on next render. Used by the
 * "Change consent" button on /cookies.
 */
export function clearConsent() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: null }));
  } catch {
    // ignore
  }
}

/** @returns {boolean} true if the user has explicitly granted analytics consent */
export function hasAnalyticsConsent() {
  const c = getConsent();
  return c !== null && c.analytics === true;
}

/** @returns {boolean} true if the user has explicitly granted marketing consent */
export function hasMarketingConsent() {
  const c = getConsent();
  return c !== null && c.marketing === true;
}

/** @returns {boolean} true if the user has not yet made a consent choice */
export function shouldShowBanner() {
  return getConsent() === null;
}

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 *
 * @param {(state: ConsentState | null) => void} callback
 * @returns {() => void}
 */
export function subscribe(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = (e) => {
    callback(e?.detail ?? null);
  };
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

// Expose on window so the legacy /cookies page React component (which
// pulls helpers off `window`) can call clearConsent without an import.
if (typeof window !== 'undefined') {
  window.crayoConsent = {
    getConsent,
    setConsent,
    clearConsent,
    hasAnalyticsConsent,
    hasMarketingConsent,
    shouldShowBanner,
    subscribe,
  };
}
